import { CheckboxItemType } from '@wix/editor-platform-feature-store';
import {
  ActionableElement,
  ComponentsPlatformService,
} from '@wix/editor-platform-manifest-services';
import { CompRef } from '@wix/document-services-types';
import { MetadataStore } from '../MetadataStore';
import { generateUniqueId } from '../utils';
import { IElementItemsBuilder, ManifestAPI } from '../types';
import { CSS_PROPERTIES } from '@wix/component-protocol/schema';

import {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

const MAX_DEPTH = 3;

export class BuilderElementItemsBuilder implements IElementItemsBuilder {
  constructor(
    private readonly editorPointerAPI: EditorPointerServiceAPI,
    private readonly behaviors: ComponentsPlatformService['behaviors'],
    private readonly metadataStore: MetadataStore,
  ) {}

  buildElementItems(
    elements: ActionableElement[],
    api: ManifestAPI,
    depth: number,
  ): CheckboxItemType[] {
    return elements.flatMap((element) =>
      this.#buildElementItem(element, api, depth),
    );
  }

  #hasValidDisplayProperty(
    elementEditorPointer: EditorPointer<CompRef>,
    api: ManifestAPI,
  ): boolean {
    const styleDefinitions = api.node
      .extract(elementEditorPointer)
      .getAPI()
      .resolver.getStyleDefinitions();

    const hasDisplayProperty =
      styleDefinitions.cssProperties[
        CSS_PROPERTIES.CSS_PROPERTY_TYPE.display
      ]?.display?.displayValues?.some((v: string) => v === 'none') ?? false;

    return hasDisplayProperty;
  }

  #isRemovable(elementEditorPointer: EditorPointer<CompRef>): boolean {
    return this.behaviors.getValue(elementEditorPointer, 'removable') ?? true;
  }

  #buildCssCustomPropertyItems(
    elementEditorPointer: EditorPointer<CompRef>,
    api: ManifestAPI,
  ): CheckboxItemType[] {
    const extractedAPI = api.node.extract(elementEditorPointer).getAPI();
    const styles = extractedAPI.query.getStyles();
    const styleDefinitions = extractedAPI.resolver.getStyleDefinitions();

    return Object.entries(styleDefinitions.cssCustomProperties ?? {})
      .filter(([, value]) =>
        value?.display?.displayValues?.some((v: string) => v === 'none'),
      )
      .map(([cssPropertyKey, value]) => {
        const id = generateUniqueId(
          this.editorPointerAPI,
          elementEditorPointer,
          cssPropertyKey,
        );
        const rawValue = styles.cssCustomProperties?.[cssPropertyKey];

        this.metadataStore.setItem(id, {
          editorPointer: elementEditorPointer,
          visibilityEditorPointer: elementEditorPointer,
          namespace: 'cssCustomProperties',
          key: cssPropertyKey,
          preservedVisibleStyleValue:
            rawValue === 'none' ? undefined : rawValue,
        });

        return {
          displayName: value.displayName ?? cssPropertyKey,
          id,
          isRemovable: true,
        };
      });
  }

  #buildElementItem(
    element: ActionableElement,
    api: ManifestAPI,
    depth: number,
  ): CheckboxItemType[] {
    if (depth > MAX_DEPTH) {
      return [];
    }

    const elementEditorPointer = element.editorPointers[0];
    const cssCustomPropertyItems = this.#buildCssCustomPropertyItems(
      elementEditorPointer,
      api,
    );

    if (
      !element.isActionable &&
      cssCustomPropertyItems.length + element.elements.length === 1
    ) {
      if (element.elements.length === 1) {
        return this.buildElementItems(element.elements, api, depth);
      }
      return cssCustomPropertyItems;
    }

    const hasValidDisplayProperty = this.#hasValidDisplayProperty(
      elementEditorPointer,
      api,
    );

    const isRemovable =
      element.isActionable &&
      this.#isRemovable(elementEditorPointer) &&
      hasValidDisplayProperty;

    const children = element.elements.length
      ? this.buildElementItems(element.elements, api, depth + 1)
      : [];

    if (!isRemovable) {
      if (depth <= 2 && children.length > 1) {
        return [
          ...cssCustomPropertyItems,
          {
            displayName: element.displayName ?? '',
            id: generateUniqueId(this.editorPointerAPI, elementEditorPointer),
            isRemovable: false,
            children,
          },
        ];
      }

      if (cssCustomPropertyItems.length === 0 || children.length === 1) {
        return [...cssCustomPropertyItems, ...children];
      }

      return cssCustomPropertyItems;
    }

    const id = generateUniqueId(this.editorPointerAPI, elementEditorPointer);
    const styles = api.node
      .extract(elementEditorPointer)
      .getAPI()
      .query.getStyles();

    const rawStyleValue =
      styles.cssProperties[CSS_PROPERTIES.CSS_PROPERTY_TYPE.display];

    this.metadataStore.setItem(id, {
      editorPointer: elementEditorPointer,
      visibilityEditorPointer: elementEditorPointer,
      namespace: 'cssProperties',
      key: CSS_PROPERTIES.CSS_PROPERTY_TYPE.display,
      preservedVisibleStyleValue:
        rawStyleValue === 'none' ? undefined : rawStyleValue,
    });

    return [
      ...cssCustomPropertyItems,
      {
        displayName: element.displayName ?? '',
        id,
        isRemovable: true,
        children,
      },
    ];
  }
}
